/* lib/wizardBridge.js — the v4.56 "Room-level material bridge → Kitchen
 * Wizard" (window.LHCMat.rooms, cabinet.html 58029-58109), ported over the
 * store blob.
 *
 * RULE (verbatim from the prototype): the Material Library is the only source
 * of materials; the Products page decides what is allowed; the Wizard is
 * ROOM-LEVEL, so it offers the UNION of every option Products allows across
 * the kitchen products that are in the Catalog. Cabinet-level hardware stays
 * out of the Wizard.
 *
 * Option ids use the catalog id scheme the inheritance functions in
 * pricing.js (inheritRoomDefaults/kw*Idx) resolve:
 *   shell  "carc-K"   → blob.colors[K]
 *   front  "pgN"      → blob.groups where .g === N
 *   gola   "gola-K"   → blob.gola[K]
 *   plinth <key>      → blob.pmat where .key === value
 *   handle <code>     → blob.handles where .code === value
 * Consumers that cannot resolve an id must SKIP it (never guess). */
import { unitGroup } from "./units";
import { tokFor as _uidTokFor } from "../../configurator/engine/catalogIds";
import { isUnitLive, isUnitPublished } from "../../configurator/engine/catalogBridge";
import { allowedCarcIdx, allowedGroupIdx, allowedGolaIdx, allowedPlinthIdx, compAllowedIdx } from "./pricing";
import { lightingTypesFor } from "./units";
import { optAllows } from "./pricing";
import { fabricOptionsFor, fabricZonesFor, fabricToken, FABRIC_ZONE_LABEL } from "../../configurator/engine/fabrics";

function swatch(c) {
  if (!c) return "#9A9BA0";
  if (c.tex) return "url(" + c.tex + ") center/cover";
  return c.sw || "#9A9BA0";
}

/* `cat` on a door is a grouping key (colors / woods / stones / metals), not a
 * caption — map it to something readable instead of printing it raw. */
const CATLBL = {
  colors: "Colour", colours: "Colour", woods: "Wood", wood: "Wood", stones: "Stone", stone: "Stone",
  metals: "Metal", metal: "Metal", glass: "Glass", solid: "Solid", specials: "Special",
};
function catLabel(v) {
  if (v == null || v === "") return "";
  const k = String(v).toLowerCase();
  return CATLBL[k] || String(v);
}
function colourList(arr, label, idFn) {
  return (arr || []).map((c, ci) => {
    const sub = (c.colours != null) ? (c.colours + " colours") : (catLabel(c.cat) || label || "");
    // batch 8: the id is the Module Configurator's own id when the caller says so (front
    // "pgN-c<tok>", plinth "pmc-<tok>-<tok>") — the wizard pick IS the module default
    return { id: idFn ? idFn(c, ci) : "c" + ci, name: c.name, color: swatch(c), sub, tag: (c.cat != null ? String(c.cat).toLowerCase() : "") };
  });
}

/** the family (Kitchen / Systems / Indoor / Outdoor) → the technician's unit group */
export const FAMILY_GROUP = { kitchen: "kitchens", kitchens: "kitchens", systems: "systems", indoor: "indoor", outdoor: "outdoor" };

/** buildWizardMaterialCats(blob) → the category/section/option tree the room
 *  wizard consumes (prototype LHCMat.rooms() shape, verbatim semantics). */
export function buildWizardMaterialCats(blob, family = "kitchen") {
  const units = [];
  const grp = FAMILY_GROUP[String(family || "kitchen").toLowerCase()] || "kitchens";
  // THE shared publication predicate (2026-07-26) — never re-implement the filter.
  // batch 8 (2026-09-28): scoped to THIS family's products — the wizard serves every family.
  (blob.carcass || []).forEach((u) => { if (isUnitPublished(u) && unitGroup(u) === grp) units.push(u); });
  if (!units.length) (blob.carcass || []).forEach((u) => { if (isUnitLive(u) && unitGroup(u) === grp) units.push(u); });
  // a sparse technician blob (a family with no colours / groups / … yet) must not throw —
  // every collection reads as an array, empty when absent
  const L = (k) => (Array.isArray(blob[k]) ? blob[k] : []);
  blob = { ...blob, colors: L("colors"), groups: L("groups"), handles: L("handles"), gola: L("gola"), pmat: L("pmat"),
    worktops: L("worktops"), backsplash: L("backsplash"), handleColors: L("handleColors"), golaColors: L("golaColors"),
    sidePanelColors: L("sidePanelColors") };

  function union(total, idxFn) {
    const seen = {};
    units.forEach((u) => { try { (idxFn(u) || []).forEach((k) => { seen[k] = 1; }); } catch (e) { /* skip */ } });
    const out = [];
    for (let k = 0; k < total; k++) { if (seen[k]) out.push(k); }
    return out;
  }

  const cats = [];
  function sect(key, title, options) { return { key, title, options }; }

  const secCarc = sect("shell", "Carcasses colour", union(blob.colors.length, (u) => allowedCarcIdx(blob, u)).map((k) => {
    const c = blob.colors[k];
    return { id: "carc-" + _uidTokFor(c, k), name: c.name, sub: "Carcass finish", color: swatch(c) };
  }));
  const secFront = sect("front", "Front group", union(blob.groups.length, (u) => allowedGroupIdx(blob, u)).map((k) => {
    const g = blob.groups[k];
    return {
      id: "pg" + g.g, name: g.name, sub: g.finish || ("PG" + g.g), color: swatch((g.doors || [])[0]),
      colorLabel: "Front finish", colors: colourList(g.doors, "", (d, di) => "pg" + g.g + "-c" + _uidTokFor(d, di)),
    };
  }));
  const secHinge = sect("hinge", "Hinges", union(blob.handles.length, (u) => compAllowedIdx(blob, u, "handles")).map((k) => {
    const h = blob.handles[k];
    return { id: h.code, name: h.name, sub: (h.finish || h.type || ""), color: "linear-gradient(180deg,#E3E4E6,#8A8C90)" };
  }));
  const secHandle = sect("handle", "Handles", union(blob.handles.length, (u) => compAllowedIdx(blob, u, "handles")).map((k) => {
    const h = blob.handles[k];
    return { id: h.code, name: h.name, sub: (h.finish || h.type || ""), color: "linear-gradient(180deg,#E3E4E6,#8A8C90)" };
  }));
  const secGola = sect("gola", "Gola", union(blob.gola.length, (u) => allowedGolaIdx(blob, u)).map((k) => {
    const g = blob.gola[k];
    return { id: "gola-" + _uidTokFor(g, k), name: g.name, sub: g.finish || "", color: swatch(g) };
  }));
  const secPlinth = sect("plinth", "Plinth", union(blob.pmat.length, (u) => allowedPlinthIdx(blob, u)).map((k) => {
    const m = blob.pmat[k];
    return {
      id: (m.key || ("plinth-" + _uidTokFor(m, k))), name: m.name, sub: m.desc || "", color: swatch((m.colors || [])[0]),
      colorLabel: "Plinth colour", colors: colourList(m.colors, "Plinth", (c, ci) => "pmc-" + _uidTokFor(m, k) + "-" + _uidTokFor(c, ci)),
    };
  }));

  const ltn = { drawer: "Drawer", vertical: "Vertical", horizontal: "Horizontal" };
  const lseen = {};
  units.forEach((u) => {
    if (u.lightCap === false) return;
    try { lightingTypesFor(u).forEach((t) => { if (optAllows(u, "lighting", t)) lseen[t] = 1; }); } catch (e) { /* skip */ }
  });
  const lopts = [{ id: "none", name: "No lighting", sub: "Off", color: "#1D1F22" }];
  ["drawer", "vertical", "horizontal"].forEach((t) => {
    if (lseen[t]) lopts.push({ id: t, name: ltn[t] + " lighting", sub: "LED · warm white", color: "linear-gradient(135deg,#F5E7C0,#D8B978)" });
  });
  const secLight = sect("lighting", "Lighting", lopts);

  // batch 8: the SHARED colour pools (technician Handles / Gola / Side panels pages) — the
  // same ids the Module Configurator stores (hc-/gc-/spc- + uid token)
  const secHandleColor = sect("handleColor", "Handle finish", blob.handleColors.map((c, k) => ({
    id: "hc-" + _uidTokFor(c, k), name: (c && c.name) || ("Finish " + (k + 1)), sub: "Handle finish", color: swatch(c),
  })));
  const secGolaColor = sect("golaColor", "Gola colour", blob.golaColors.map((c, k) => ({
    id: "gc-" + _uidTokFor(c, k), name: (c && c.name) || ("Finish " + (k + 1)), sub: "Gola colour", color: swatch(c),
  })));
  const secSidePanelColor = sect("sidePanelColor", "Side panel colour", blob.sidePanelColors.map((c, k) => ({
    id: "spc-" + _uidTokFor(c, k), name: (c && c.name) || ("Colour " + (k + 1)), sub: "Side panel", color: swatch(c),
  })));

  // worktops / backsplash are NAME-keyed everywhere else (config.countertop, roomWorktop,
  // the surfaces oracle, the sheets) — the wizard id is the name
  const secWt = sect("worktop", "Worktops", union((blob.worktops || []).length, (u) => compAllowedIdx(blob, u, "worktops")).map((k) => {
    const w = blob.worktops[k];
    return {
      id: w.name, name: w.name, sub: ((w.colors || []).length + " colours"), color: swatch((w.colors || [])[0]),
      colorLabel: "Worktop colour", colors: colourList(w.colors, "Worktop"),
    };
  }));
  const secBs = sect("backsplash", "Backsplash", union((blob.backsplash || []).length, (u) => compAllowedIdx(blob, u, "backsplash")).map((k) => {
    const b = blob.backsplash[k];
    return {
      id: b.name, name: b.name, sub: ((b.colors || []).length + " colours"), color: swatch((b.colors || [])[0]),
      colorLabel: "Backsplash colour", colors: colourList(b.colors, "Backsplash"),
    };
  }));

  cats.push({ key: "carcasses", title: "Carcasses", desc: "Structural shell of every cabinet.", icon: "box", sections: [secCarc] });
  cats.push({ key: "fronts", title: "Fronts", desc: "Door / front material group.", icon: "palette", sections: [secFront, secHinge] });
  cats.push({ key: "handles", title: "Handles", desc: "How drawers and doors open.", icon: "handle", sections: [secHandle, secHandleColor, secGola, secGolaColor] });
  cats.push({ key: "sidepanels", title: "Side panels", desc: "Decorative end panels.", icon: "box", sections: [secSidePanelColor] });
  cats.push({ key: "plinth", title: "Plinth", desc: "The recessed kickplate under base units.", icon: "plinth", sections: [secPlinth] });
  cats.push({ key: "lighting", title: "Lighting", desc: "Integrated LED lighting.", icon: "palette", sections: [secLight] });
  cats.push({ key: "worktops", title: "Worktops", desc: "Work surface and the wall behind it.", icon: "top", sections: [secWt, secBs] });

  /* FABRIC (batch 14, 2026-10-03): an Indoor / Outdoor project picks ONE fabric for every
   * sofa, armchair and lounger it adds — the designs of every Fabric Library group at least
   * one of the family's products allows (Indoor: Indoor Fabric + Leather · Outdoor: Outdoor
   * Fabric + Rope); the colour ids are the module's own config.fabric tokens. */
  const famZones = fabricZonesFor(grp);
  if (famZones.length) {
    const seenG = {};
    units.forEach((u) => { try { fabricOptionsFor(blob, u).forEach((z) => z.groups.forEach((g) => { seenG[z.zone + "|" + g.key] = 1; })); } catch (e) { /* skip */ } });
    const fopts = [];
    famZones.forEach((zone) => (((blob.fabrics || {})[zone]) || []).forEach((g) => {
      if (!g || !seenG[zone + "|" + g.key]) return;
      (g.designs || []).forEach((d, di) => {
        const colors = ((d && d.colors) || []).map((c, ci) => ({ id: fabricToken(zone, g.key, di, ci), name: (c && c.name) || ("Colour " + (ci + 1)), color: swatch(c) }));
        if (!colors.length) return;
        fopts.push({ id: "fd:" + zone + ":" + g.key + ":" + di, name: (d && d.name) || ("Design " + (di + 1)),
          sub: FABRIC_ZONE_LABEL[zone] + " · " + (g.label || g.key), color: colors[0].color, colorLabel: "Fabric colour", colors });
      });
    }));
    cats.unshift({ key: "fabric", title: "Fabric", desc: "Upholstery of every sofa, armchair and lounger.", icon: "palette", sections: [sect("fabric", "Fabric", fopts)] });
  }

  // Drop a page only when the Material Library gives it nothing at all.
  cats.forEach((c) => { c.sections = c.sections.filter((s) => s.options && s.options.length); });
  return cats.filter((c) => c.sections.length);
}

/** productForType(blob, type) — find the catalog product matching a placed
 *  module's type/code (LHC_MATLIB bridge, cabinet.html 62569-62583): exact
 *  SKU match, then case-insensitive, then with the -L/-R suffix stripped. */
export function productForType(blob, type) {
  if (!type) return null;
  const C = blob.carcass || [];
  const codeUp = String(type).toUpperCase();
  for (let i = 0; i < C.length; i++) { if (String(C[i].sku).toUpperCase() === codeUp) return C[i]; }
  const stripped = codeUp.replace(/-[LR]$/, "");
  for (let j = 0; j < C.length; j++) {
    const s = String(C[j].sku).toUpperCase();
    if (s === stripped || s.replace(/-[LR]$/, "") === stripped) return C[j];
  }
  return null;
}
